import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Guid from "_shared/guids/guid";
import { z } from "zod";

export type StaffingOutcomeJSON = z.infer<typeof StaffingOutcomeSchema.StaffingOutcomeJSONZ>;
export type StaffingOutcomeParams = z.infer<typeof StaffingOutcomeSchema.StaffingOutcomeZ>;

/**
 * What became of an attempt to put somebody onto a piece of work.
 *
 * Three outcomes share one shape because the caller has to tell them apart before it says anything:
 * seated, already there, or not seated because a revision opened instead. `revisionProposalId` is
 * the negotiation to open in that last case -- an agreement whose roster both parties agreed to
 * cannot be re-staffed in place.
 */
export default class StaffingOutcomeSchema {
  public static readonly StaffingOutcomeJSONZ = z.object({
    destinationId: z.uuid(),
    kind: z.enum(["Proposal", "WorkAgreement"]),
    name: z.string(),
    alreadyPresent: z.boolean(),
    revisionProposalId: z.uuid().nullish(),
  });

  public static readonly StaffingOutcomeZ = z.object({
    destinationId: z.instanceof(Guid),
    kind: z.enum(["Proposal", "WorkAgreement"]),
    name: z.string(),
    alreadyPresent: z.boolean(),
    revisionProposalId: z.instanceof(Guid).optional(),
  });

  public static validate(values: unknown): StaffingOutcomeParams {
    return parseOrThrow(StaffingOutcomeSchema.StaffingOutcomeZ, values, {
      logError: true,
      schemaName: "StaffingOutcomeSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): StaffingOutcomeJSON {
    return parseOrThrow(StaffingOutcomeSchema.StaffingOutcomeJSONZ, json, {
      logError: true,
      schemaName: "StaffingOutcomeSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValidJSON(json: unknown): json is StaffingOutcomeJSON {
    try {
      StaffingOutcomeSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: StaffingOutcomeJSON): StaffingOutcomeParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(StaffingOutcomeSchema.StaffingOutcomeJSONZ, json, {
      logError: true,
      schemaName: "StaffingOutcomeSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return StaffingOutcomeSchema.codec.decode(parsed);
  }

  public static serialize(params: StaffingOutcomeParams): StaffingOutcomeJSON {
    if (isNullOrUndefined(params)) return params;
    const parsed = StaffingOutcomeSchema.validate(params);
    if (isNullOrUndefined(parsed)) return parsed;
    return StaffingOutcomeSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    StaffingOutcomeSchema.StaffingOutcomeJSONZ,
    StaffingOutcomeSchema.StaffingOutcomeZ,
    {
      decode: (data) => ({
        destinationId: Guid.fromJSON(data.destinationId)!,
        kind: data.kind,
        name: data.name,
        alreadyPresent: data.alreadyPresent,
        revisionProposalId: Guid.fromJSON(data.revisionProposalId) ?? undefined,
      }),
      encode: (outcome) => ({
        destinationId: outcome.destinationId.toJSON(),
        kind: outcome.kind,
        name: outcome.name,
        alreadyPresent: outcome.alreadyPresent,
        revisionProposalId: isNullOrUndefined(outcome.revisionProposalId)
          ? null
          : outcome.revisionProposalId.toJSON(),
      }),
    }
  );
}
